/**
 * User drill-down TanStack Query hooks (Plan 04-09 Task 1).
 *
 * One hook per endpoint — matches the convention of `use-operations.ts` /
 * `use-quality.ts`. Every hook unwraps the server envelope in its
 * `queryFn` and returns the inner payload through `useQuery`.
 *
 * Key rules:
 * `staleTime: 60_000` + `placeholderData: keepPreviousData` — and since the
 * recount rule, the second of those no longer means the previous filter set's
 * answer stays on screen. `useFilteredQuery` reports that window as "nothing
 * counted yet" (hooks/use-filtered-query.ts), so the tab blanks and says so.
 * What `keepPreviousData` still buys: telling a recount apart from a first
 * load, and an instant switch back to a filter set already in the cache.
 *   - All hooks gate on `enabled: !!username` — the drilldown mounts via
 *     `useParams()` which is `undefined` mid-route-transition.
 *   - `useUserProfile` sets `retry: false` so a 404 lands on the UI on the
 *     first attempt (no backoff), and the hook re-throws the typed
 *     `UserNotFoundError` (caller checks `error instanceof UserNotFoundError`).
 *
 * Phase 8 Plan 04 removed the bespoke per-user activity-feed and
 * feedback-timeline hooks. The Activity tab and the UserDetail Feedback
 * tab now consume the shared `useTracesList` (`/salescoach/api/traces/list`)
 * with a per-user `users:[username]` filter.
 */

import { keepPreviousData } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import { resolveRange, filtersQuestion, type Filters } from '@/lib/filters-schema';
import {
  fetchUserCharts,
  fetchUserKpis,
  fetchUserProfile,
  fetchUserSafetyRefusals,
  fetchUserTopSkills,
  fetchUserTopTools,
  UserNotFoundError,
  type TopListParams,
} from '@/services/user-api';
import type {
  UserChartsData,
  UserChartsSource,
  UserKPIs,
  UserProfile,
  UserSafetyRefusalRow,
  UserSkillRow,
  UserToolRow,
} from '@/services/api-types';

const STALE_TIME = 60_000;

// Re-export the typed error so page-level code can `instanceof`-check.
export { UserNotFoundError } from '@/services/user-api';

/**
 * Profile — lifetime-scoped, no filter params. `retry: false` lets a 404
 * surface immediately; the hook's `isError` + `error instanceof
 * UserNotFoundError` branches the page into the "User not found" screen.
 */
export function useUserProfile(username: string | undefined) {
  return useFilteredQuery<UserProfile, UserNotFoundError | Error>({
    queryKey: ['user', 'profile', username ?? null],
    queryFn: async () => {
      if (!username) throw new Error('username is required');
      return (await fetchUserProfile(username)).data;
    },
    enabled: !!username,
    retry: false,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useUserKpis(username: string | undefined, filters: Filters) {
  // Resolved [from, to] joins the query key so presets like '7d' reliably
  // invalidate when the day rolls over (see use-quality.ts / use-feedback.ts
  // for the same pattern).
  const range = resolveRange(filters);
  return useFilteredQuery<UserKPIs>({
    queryKey: [
      'user',
      'kpis',
      username ?? null,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => {
      if (!username) throw new Error('username is required');
      return (await fetchUserKpis(username, filters)).data;
    },
    enabled: !!username,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useUserCharts(
  username: string | undefined,
  filters: Filters,
  granularity: Filters['granularity'],
  source: UserChartsSource = 'all',
) {
  const range = resolveRange(filters);
  return useFilteredQuery<UserChartsData>({
    // `source` participates in the key so flipping the Overview segmented
    // control triggers a distinct cache entry — briefs-only and reactive-
    // only responses zero the other series server-side, so we can't reuse
    // the `all` payload and filter client-side.
    queryKey: [
      'v2',
      'user',
      'charts',
      username ?? null,
      granularity,
      source,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => {
      if (!username) throw new Error('username is required');
      return (await fetchUserCharts(username, filters, granularity, source))
        .data;
    },
    enabled: !!username,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useUserTopTools(
  username: string | undefined,
  filters: Filters,
  params: TopListParams = {},
) {
  const range = resolveRange(filters);
  return useFilteredQuery<UserToolRow[]>({
    queryKey: [
      'v2',
      'user',
      'top-tools',
      username ?? null,
      params.limit ?? null,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => {
      if (!username) throw new Error('username is required');
      return (await fetchUserTopTools(username, filters, params)).data;
    },
    enabled: !!username,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useUserTopSkills(
  username: string | undefined,
  filters: Filters,
  params: TopListParams = {},
) {
  const range = resolveRange(filters);
  return useFilteredQuery<UserSkillRow[]>({
    queryKey: [
      'v2',
      'user',
      'top-skills',
      username ?? null,
      params.limit ?? null,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => {
      if (!username) throw new Error('username is required');
      return (await fetchUserTopSkills(username, filters, params)).data;
    },
    enabled: !!username,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useUserSafetyRefusals(
  username: string | undefined,
  filters: Filters,
) {
  const range = resolveRange(filters);
  return useFilteredQuery<UserSafetyRefusalRow[]>({
    queryKey: [
      'v2',
      'user',
      'safety-refusals',
      username ?? null,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => {
      if (!username) throw new Error('username is required');
      return (await fetchUserSafetyRefusals(username, filters)).data;
    },
    enabled: !!username,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}
